import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, X } from 'lucide-react';
import type { CursorPage, EmployeeRow } from '@dru/types';
import { EMPLOYMENT_STATUS_LABEL } from '@dru/types';
import { cn } from '@dru/ui';
import { api } from '../lib/api';

/**
 * The header's "find a worker": type an employee code (or a name or mobile),
 * pick the row, land on the employee's page. An exact code match opens on
 * Enter without a second step, because the code is what HR has in hand.
 */
export function EmployeeSearch({ className, autoFocus, onNavigate }: { className?: string; autoFocus?: boolean; onNavigate?: () => void }) {
  const navigate = useNavigate();
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<EmployeeRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows(null); setError(null); return; }
    let live = true;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const page = await api.get<CursorPage<EmployeeRow>>(`/employees?limit=8&q=${encodeURIComponent(term)}`);
        if (!live) return;
        const exact = term.toLowerCase();
        // The exact code first: that is the row the person typed.
        const sorted = [...page.items].sort((a, b) =>
          Number(b.employeeCode.toLowerCase() === exact) - Number(a.employeeCode.toLowerCase() === exact));
        setRows(sorted); setError(null); setActive(0);
      } catch {
        if (live) { setRows([]); setError('Search failed. Try again.'); }
      } finally {
        if (live) setBusy(false);
      }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const go = (row: EmployeeRow) => {
    setOpen(false); setQ(''); setRows(null);
    input.current?.blur();
    navigate(`/hr/employees/${row.id}`);
    onNavigate?.();
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (!rows?.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, rows.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); go(rows[active] ?? rows[0]!); }
  };

  const showList = open && q.trim().length >= 2;

  return (
    <div ref={box} className={cn('relative', className)}>
      <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-label="Search employees by employee ID, name or mobile"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && rows?.length ? `${listId}-${active}` : undefined}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search employee ID, name…"
        autoComplete="off"
        autoFocus={autoFocus}
        className="h-9 w-full rounded-btn border border-neutral-200 bg-neutral-50 pl-8 pr-8 text-body text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100 [&::-webkit-search-cancel-button]:hidden"
      />
      {busy ? (
        <Loader2 aria-hidden className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-neutral-400" />
      ) : q ? (
        <button type="button" aria-label="Clear search" onClick={() => { setQ(''); input.current?.focus(); }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-neutral-400 hover:text-neutral-700">
          <X className="h-4 w-4" />
        </button>
      ) : null}

      {showList && (
        <div id={listId} role="listbox"
          className="absolute left-0 right-0 top-full z-40 mt-1 max-h-96 overflow-auto rounded-card border border-neutral-200 bg-white py-1 shadow-lg sm:min-w-[22rem]">
          {error && <p className="px-3 py-2 text-caption text-danger-600">{error}</p>}
          {!error && rows && rows.length === 0 && !busy && (
            <p className="px-3 py-2 text-caption text-neutral-500">No employee matches “{q.trim()}”.</p>
          )}
          {!rows && busy && <p className="px-3 py-2 text-caption text-neutral-500">Searching…</p>}
          {rows?.map((r, i) => (
            <button
              key={r.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => go(r)}
              className={cn('flex w-full items-center gap-3 px-3 py-2 text-left', i === active ? 'bg-primary-50' : 'hover:bg-neutral-50')}
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-caption font-semibold text-neutral-600">
                {r.fullName.slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-body font-medium text-neutral-900">{r.fullName}</span>
                <span className="block truncate text-caption text-neutral-500">
                  <span className="font-mono text-neutral-700">{r.employeeCode}</span>
                  {r.mobile ? ` · ${r.mobile}` : ''}
                </span>
              </span>
              <span className="shrink-0 text-caption text-neutral-500">
                {EMPLOYMENT_STATUS_LABEL[r.status as keyof typeof EMPLOYMENT_STATUS_LABEL] ?? r.status}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
